/**
 * Wave chat-instant · client wrapper para /v1/chat/*.
 *
 * Separado de `api` (em client.ts) e de `chatApi` (Wave chat-mvp, que
 * usa as rotas legacy /v1/messages/chat/*). Este wrapper bate nas
 * rotas novas montadas em /v1/chat por chat-instant.ts no API.
 *
 * Tudo passa pelo `request<T>` shared do client.ts pra reaproveitar:
 *   - injeção do Bearer token
 *   - bounce automático ao /login em 401
 *   - unwrap de payload.data
 *
 * O backend já valida tudo (rate limit, membership, tenant) — aqui só
 * documentamos o shape do que volta.
 */

import { request } from './client';

// ── Tipos públicos (espelham chat-instant.service.ts) ────────────────

export interface ChatInstantReactionGroup {
  emoji: string;
  users: string[];
}

export interface ChatInstantMessage {
  id: string;
  conversationId: string;
  conversationType: 'dm' | 'group';
  authorUserId: string;
  body: string;
  replyToId: string | null;
  createdAt: string;
  expiresAt: string;
  deletedAt: string | null;
  reactions: ChatInstantReactionGroup[];
}

export interface ChatInstantMember {
  userId: string;
  fullName: string;
  avatarUrl: string | null;
  notificationEnabled: boolean;
}

export interface ChatInstantConversation {
  id: string;
  type: 'dm' | 'group';
  name: string | null;
  projectId: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  members: ChatInstantMember[];
  lastMessage: ChatInstantMessage | null;
  unreadCount: number;
  peerOnline?: boolean;
}

export interface ChatInstantPresence {
  online: boolean;
  lastSeenAt: string | null;
  isTypingIn: string | null;
}

// ── API wrapper ──────────────────────────────────────────────────────

export const chatInstantApi = {
  /** Cria-ou-busca a DM entre o user atual e `peerUserId`. Idempotente. */
  openDM: (peerUserId: string) =>
    request<{ conversationId: string }>('/v1/chat/dm', {
      method: 'POST',
      body: JSON.stringify({ peerUserId }),
    }),

  /** Lista todas as conversations do user — DMs e groups. Ordenada por
   *  last_message_at desc. Inclui unread count, peerOnline (DMs), e
   *  preview da última mensagem. */
  listConversations: () =>
    request<ChatInstantConversation[]>('/v1/chat/conversations'),

  /** Histórico de mensagens. `before` é cursor ISO; default 50, max 200. */
  getMessages: (conversationId: string, opts: { before?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.before) p.set('before', opts.before);
    if (opts.limit) p.set('limit', String(opts.limit));
    const qs = p.toString();
    return request<ChatInstantMessage[]>(
      `/v1/chat/conversations/${encodeURIComponent(conversationId)}/messages${qs ? `?${qs}` : ''}`,
    );
  },

  /** Envia mensagem. Rate limit: 60/min/user. */
  sendMessage: (conversationId: string, body: string, replyToId?: string | null) =>
    request<ChatInstantMessage>(
      `/v1/chat/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ body, replyToId: replyToId ?? null }),
      },
    ),

  /** Marca conversation como lida — zera o unread count pra esse user. */
  markRead: (conversationId: string) =>
    request<{ ok: true }>(
      `/v1/chat/conversations/${encodeURIComponent(conversationId)}/read`,
      { method: 'POST' },
    ),

  /** Adiciona reação. INSERT OR IGNORE — duplicate é noop. */
  addReaction: (messageId: string, emoji: string) =>
    request<{ ok: true }>(
      `/v1/chat/messages/${encodeURIComponent(messageId)}/reactions`,
      {
        method: 'POST',
        body: JSON.stringify({ emoji }),
      },
    ),

  /** Remove a reação do user. */
  removeReaction: (messageId: string, emoji: string) =>
    request<{ ok: true }>(
      `/v1/chat/messages/${encodeURIComponent(messageId)}/reactions/${encodeURIComponent(emoji)}`,
      { method: 'DELETE' },
    ),

  /** Soft delete — só o author. */
  deleteMessage: (messageId: string) =>
    request<{ ok: true }>(
      `/v1/chat/messages/${encodeURIComponent(messageId)}`,
      { method: 'DELETE' },
    ),

  /** Heartbeat de presence. `typingIn` = conversationId quando o user está
   *  digitando, null quando parou. Backend marca last_seen_at = now. */
  ping: (typingIn?: string | null) =>
    request<{ ok: true }>('/v1/chat/presence/ping', {
      method: 'POST',
      body: JSON.stringify({ typingIn: typingIn ?? null }),
    }),

  /** Bulk presence lookup. Retorna { userId: { online, lastSeenAt, isTypingIn } }. */
  getPresence: (userIds: string[]) => {
    if (userIds.length === 0) {
      return Promise.resolve({} as Record<string, ChatInstantPresence>);
    }
    return request<Record<string, ChatInstantPresence>>(
      `/v1/chat/presence?userIds=${encodeURIComponent(userIds.join(','))}`,
    );
  },
};
